"use client";

import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FormItem, FormLabel, FormControl, FormMessage, FormField } from "@/components/ui/form";
import { Dictionary, SupportedLanguages } from "@/types/dictionary";
import { useLang } from "@/app/context/LangContext";
import Link from "next/link";
import { Video } from "lucide-react";
import { format } from "date-fns";
import Loader from "../loader";
import { useRef } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";

// Validation copy follows the page language - an English "Invalid email" on
// the RTL Hebrew funnel read like a system glitch, not guidance.
const validationCopy = (isHe: boolean) =>
  isHe
    ? {
        nameRequired: 'נא למלא שם',
        emailInvalid: 'כתובת האימייל לא תקינה',
        phoneRequired: 'נא למלא מספר טלפון',
        phoneInvalid: 'מספר הטלפון לא תקין',
        privacyRequired: 'יש לאשר את מדיניות הפרטיות',
      }
    : {
        nameRequired: 'Name is required',
        emailInvalid: 'Invalid email',
        phoneRequired: 'Phone is required',
        phoneInvalid: 'Please enter a valid phone number',
        privacyRequired: 'You must accept the privacy policy',
      };

const createBookingFormSchema = (isPhoneRequired: boolean = true, isPrivacyPolicyRequired: boolean = true, isHe: boolean = false) => {
  const t = validationCopy(isHe);
  return z.object({
    name: z.string().trim().min(1, t.nameRequired),
    // .trim() mirrors what browsers already do for type="email" inputs, and
    // protects any non-browser caller. It does not loosen the format check.
    email: z.string().trim().email(t.emailInvalid),
    phone: isPhoneRequired ? z
      .string()
      .trim()
      .min(7, t.phoneRequired)
      .regex(/^[0-9+\-\s()]+$/, t.phoneInvalid)
      : z.string().optional(),
    message: z.string().optional(),
    acceptedPrivacyPolicy: isPrivacyPolicyRequired ? z.boolean().refine(val => val === true, {
      message: t.privacyRequired
    }) : z.boolean().optional(),
  });
};

export type BookingFormValues = z.infer<ReturnType<typeof createBookingFormSchema>>;

export function ClientDetailsForm({ dict,
  onSubmit,
  labels,
  showMessage,
  selectedDate,
  selectedTime,
  bookingDetailsTitle,
  availableOnline,
  onlineMeeting,
  duration,
  isSubmitting = false,
  prefilledData,
}: {
  dict: Partial<Dictionary["calendar"]>
  onSubmit: (data: BookingFormValues) => void;
  labels: Dictionary["calendar"]["labels"];
  showMessage: boolean;
  selectedDate?: Date;
  selectedTime?: string;
  bookingDetailsTitle?: string;
  availableOnline?: string;
  onlineMeeting?: string;
  duration?: string;
  isSubmitting?: boolean;
  prefilledData?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}) {
  const lang = useLang();
  const isRtl = lang === SupportedLanguages.HE;
  const isPhoneRequired = !prefilledData?.phone; // Phone is required only if not prefilled
  const isPrivacyPolicyRequired = !prefilledData?.email; // Only require privacy policy if email is not prefilled
  const bookingFormSchema = createBookingFormSchema(isPhoneRequired, isPrivacyPolicyRequired, isRtl);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    mode: "onChange",
    defaultValues: {
      name: prefilledData?.name || "",
      email: prefilledData?.email || "",
      phone: prefilledData?.phone || "",
      message: "",
      acceptedPrivacyPolicy: !isPrivacyPolicyRequired, // Auto-accept if not required
    },
  });

  const handleFormSubmit = (data: BookingFormValues) => {
    console.log('Form submission started', { data, selectedDate, selectedTime });
    onSubmit(data);
  };

  const { trackEvent } = useAnalytics();
  const firedStarted = useRef(false);
  const onFirstFocus = () => {
    if (firedStarted.current) return;
    firedStarted.current = true;
    trackEvent('booking_form_started', { form_name: 'booking_form' });
  };

  const handleFormError = (errors: any) => {
    console.error('Form validation errors:', errors);
    const firstField = Object.keys(errors || {})[0];
    trackEvent('booking_form_validation_error', {
      form_name: 'booking_form',
      field: firstField || null,
    });
  };
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit, handleFormError)} onFocus={onFirstFocus} className="flex flex-col h-full ">
        
        <div className="flex flex-col sm:gap-4 p-2 md:p-0 flex-1">
            <div className="flex flex-col sm:gap-2 gap-6">
                  {!prefilledData?.name && (
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="relative">
                          <FormControl>
                            <div className="relative">
                              <Input
                                id="booking-name"
                                placeholder=" "
                                aria-label={labels.name}
                                className="bg-white/10 border-white/20 text-white placeholder:text-transparent focus:border-[#998BFD] focus:ring-[#998BFD]/20 rounded-lg h-12 px-4 backdrop-blur-sm peer"
                                {...field}
                              />
                              <FormLabel htmlFor="booking-name" className={`absolute transition-all duration-200 pointer-events-none text-gray-400 text-sm ${isRtl ? 'right-4' : 'left-4'} top-3 peer-focus:top-1 peer-focus:text-xs peer-focus:text-[#998BFD] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-[#998BFD]`}>
                                {labels.name}
                              </FormLabel>
                            </div>
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  )}
                  {!prefilledData?.email && (
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="relative">
                          <FormControl>
                            <div className="relative">
                              <Input
                                id="booking-email"
                                type="email"
                                placeholder=" "
                                aria-label={labels.email}
                                className="bg-white/10 border-white/20 text-white placeholder:text-transparent focus:border-[#998BFD] focus:ring-[#998BFD]/20 rounded-lg h-12 px-4 backdrop-blur-sm peer"
                                {...field}
                              />
                              <FormLabel htmlFor="booking-email" className={`absolute transition-all duration-200 pointer-events-none text-gray-400 text-sm ${isRtl ? 'right-4' : 'left-4'} top-3 peer-focus:top-1 peer-focus:text-xs peer-focus:text-[#998BFD] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-[#998BFD]`}>
                                {labels.email}
                              </FormLabel>
                            </div>
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  )}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="relative">
                      <FormControl>
                        <div className="relative">
                          <Input
                            id="booking-phone"
                            type="tel"
                            dir={isRtl ? "rtl" : "ltr"}
                            placeholder=" "
                            aria-label={labels.phone}
                            className="bg-white/10 border-white/20 text-white placeholder:text-transparent focus:border-[#998BFD] focus:ring-[#998BFD]/20 rounded-lg h-12 px-4 backdrop-blur-sm peer"
                            {...field}
                          />
                          <FormLabel htmlFor="booking-phone" className={`absolute transition-all duration-200 pointer-events-none text-gray-400 text-sm ${isRtl ? 'right-4' : 'left-4'} top-3 peer-focus:top-1 peer-focus:text-xs peer-focus:text-[#998BFD] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-[#998BFD]`}>
                            {labels.phone}
                          </FormLabel>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                {showMessage && (
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem className="relative">
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              id="booking-message"
                              placeholder=" "
                              aria-label={labels.message}
                              className="bg-white/10 border-white/20 text-white placeholder:text-transparent focus:border-[#998BFD] focus:ring-[#998BFD]/20 resize-none rounded-lg min-h-[80px] px-4 py-3 backdrop-blur-sm peer"
                              {...field}
                            />
                            <FormLabel htmlFor="booking-message" className={`absolute transition-all duration-200 pointer-events-none text-gray-400 text-sm ${isRtl ? 'right-4' : 'left-4'} top-3 peer-focus:top-1 peer-focus:text-xs peer-focus:text-[#998BFD] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-[#998BFD]`}>
                              {labels.message}
                            </FormLabel>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                )}  
                {!prefilledData?.email && (
                <FormField
                  control={form.control}
                  name="acceptedPrivacyPolicy"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start  space-y-0 pt-2">
                      <FormControl>
                        <input
                          type="checkbox"
                          id="privacy-checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          aria-label="Accept privacy policy and terms of service"
                          className="mt-1 accent-[#998BFD] "
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel htmlFor="privacy-checkbox" className="text-sm font-normal text-gray-300 px-2">
                          {labels?.privacyPolicy ? (
                            <>
                              {labels.privacyPolicy.prefix}{' '}
                                                              <Link href={`/${lang}/privacy-policy`} target="_blank" className="text-[#998BFD] hover:text-[#b8abfd] hover:underline">
                                  {labels.privacyPolicy.privacyText}
                                </Link>
                              {' '}{labels.privacyPolicy.suffix}
                            </>
                          ) : (
                            lang === SupportedLanguages.HE ? (
                              <>
                                אני מסכים ל
                                <Link href={`/${lang}/privacy-policy`} target="_blank" className="text-[#998BFD] hover:text-[#b8abfd] hover:underline mx-4 ">
                                  מדיניות הפרטיות
                                </Link>
                                ולתנאי השימוש
                              </>
                            ) : (
                              <>
                                I agree to the{' '}
                                <Link href={`/${lang}/privacy-policy`} target="_blank" className="text-[#998BFD] hover:text-[#b8abfd] hover:underline">
                                  Privacy Policy
                                </Link>
                                {' '}and Terms of Service
                              </>
                            )
                          )}
                        </FormLabel>
                        <FormMessage className="text-red-400" />
                      </div>
                    </FormItem>
                    )}
                  />
                )}
            </div>
        
        <div className="flex flex-col justify-end mt-auto sm:mb-10">
            {selectedDate && selectedTime && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 mb-6 mt-4">
            <h3 className="text-[1.5rem] sm:text-lg font-semibold mb-2 text-white">{bookingDetailsTitle}</h3>
            <p className="text-[1rem] sm:text-sm mb-1 text-gray-300">
              {format(selectedDate, "PPP")} at {selectedTime}
            </p>
            <div className="flex items-center text-[0.8rem] sm:text-sm gap-2 mb-1 text-gray-400">
              <Video className="h-4 w-4 text-[#998BFD]" /> {availableOnline}
            </div>
            <p className="text-[1rem] sm:text-sm text-gray-300">
              {onlineMeeting} • {duration}
            </p>
          </div>
        )}
            <div className="flex justify-center ">
              <Button
                type="submit"
                disabled={isSubmitting || !selectedDate || !selectedTime}
                onClick={() => console.log('Button clicked', { isSubmitting, selectedDate, selectedTime, formValid: form.formState.isValid, errors: form.formState.errors })}
                className="sm:w-[15rem] bg-[#998BFD] text-white font-bold text-[0.9rem] h-[3rem] w-[15rem] hover:bg-[#8073e6] disabled:bg-gray-600 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-500/20 border-0 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <Loader type="light" />
                ) : (
                  dict.bookingNow || 'Book Now'
                )}
              </Button>
            </div>
        </div>
            
        </div>
      </form>
    </FormProvider>
  );
}
